import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import keycloak, { keycloakInitOptions, initializeKeycloak, keycloakLoginOptions } from './config/keycloak';
import { useMockData } from './config/development';
import { apiService } from './services/api';
import { ThemeProvider } from './contexts/ThemeContext';
import { AlertProvider, useAlert } from './contexts/AlertContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import Dashboard from './components/DashboardUnified';
import RoutesPage from './components/RoutesWithSidebar';
import Domains from './components/Domains';
import Workspaces from './components/Workspaces';
import Settings from './components/SettingsUnified';
import Clickstream from './components/ClickstreamUnified';
import Layout from './components/LayoutUnified';
import LoadingSpinner from './components/LoadingSpinner';
import LoggedOut from './components/LoggedOut';
import KeycloakError from './components/KeycloakError';
import InitializationWizard from './components/InitializationWizard';
import InviteAccept from './components/InviteAccept';
import './App.css';
import './components/DesignSystem.css';

interface AppState {
  keycloakInitialized: boolean;
  authenticated: boolean;
  loading: boolean;
  error: string | null;
  needsInitialization: boolean;
  checkingInitialization: boolean;
}

function AppContent() {
  const { showToast } = useAlert();
  const [state, setState] = useState<AppState>({
    keycloakInitialized: false,
    authenticated: false,
    loading: true,
    error: null,
    needsInitialization: false,
    checkingInitialization: true,
  });

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        // Skip Keycloak initialization in mock data mode
        if (useMockData) {
          console.log('Mock data mode enabled - skipping Keycloak initialization');
          setState({
            keycloakInitialized: true,
            authenticated: true, // Mock authenticated state
            loading: false,
            error: null,
            needsInitialization: false,
            checkingInitialization: false,
          });
          return;
        }

        // Initialize Keycloak using the protected initialization function
        const authenticated = await initializeKeycloak(keycloakInitOptions);

        if (!authenticated) {
          setState({
            keycloakInitialized: true,
            authenticated: false,
            loading: false,
            error: null,
            needsInitialization: false,
            checkingInitialization: false,
          });
          return;
        }

        // Set up token refresh
        setInterval(() => {
          keycloak.updateToken(70).catch(() => {
            console.error('Failed to refresh token');
          });
        }, 60000); // Refresh every minute

        // Check if user needs to go through setup wizard
        try {
          console.log('Checking if user needs initialization...');

          // Check initialization status using the dedicated endpoint
          const initStatus = await apiService.user.getInitializationStatus();

          if (initStatus.needsInitialization) {
            console.log('User needs initialization - redirecting to setup wizard');
            console.log(`Has workspaces: ${initStatus.hasWorkspaces}, Has domains: ${initStatus.hasDomains}, Has user settings: ${initStatus.hasUserSettings}`);
          } else {
            console.log('User already initialized');
          }

          setState({
            keycloakInitialized: true,
            authenticated: true,
            loading: false,
            error: null,
            needsInitialization: initStatus.needsInitialization,
            checkingInitialization: false,
          });
        } catch (error) {
          console.error('Failed to check initialization status:', error);
          // On error, assume user needs initialization to be safe
          setState({
            keycloakInitialized: true,
            authenticated: true,
            loading: false,
            error: null,
            needsInitialization: true,
            checkingInitialization: false,
          });
        }
      } catch (error) {
        console.error('Keycloak initialization failed:', error);
        setState({
          keycloakInitialized: false,
          authenticated: false,
          loading: false,
          error: 'Failed to initialize authentication. Please try again.',
          needsInitialization: false,
          checkingInitialization: false,
        });
      }
    };

    initKeycloak();
  }, []);

  if (state.loading || state.checkingInitialization) {
    return <LoadingSpinner message={state.loading ? "Initializing authentication..." : "Checking setup status..."} />;
  }

  if (state.error) {
    return (
      <KeycloakError 
        error={state.error}
        onRetry={() => window.location.reload()}
        onEnableMockData={() => {
          showToast('Please add REACT_APP_USE_MOCK_DATA=true to your .env.local file and restart the app.', 'info');
        }}
      />
    );
  }

  return (
      <Router>
        <Routes>
        {/* Public Routes */}
        <Route path="/logged-out" element={<LoggedOut onLogin={() => keycloak.login(keycloakLoginOptions)} />} />
        <Route path="/invite/:token" element={<InviteAccept authenticated={state.authenticated} />} />

        {/* Setup Wizard - Protected but outside Layout */}
        <Route path="/setup" element={
          state.authenticated ? (
            <InitializationWizard onComplete={() => {
              // Refresh the page or update state to reflect completion
              setState(prev => ({ ...prev, needsInitialization: false }));
            }} />
          ) : (
            <Navigate to="/logged-out" replace />
          )
        } />

        {/* Protected Routes */}
        <Route path="/*" element={
          !state.authenticated ? (
            <Navigate to="/logged-out" replace />
          ) : state.needsInitialization ? (
            <Navigate to="/setup" replace />
          ) : (
            <WorkspaceProvider>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/routes" element={<RoutesPage />} />
                  <Route path="/domains" element={<Domains />} />
                  <Route path="/workspaces" element={<Workspaces />} />
                  <Route path="/clickstream" element={<Clickstream />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            </WorkspaceProvider>
          )
        } />
      </Routes>
    </Router>
  );
}

const App: React.FC = () => (
  <ThemeProvider>
    <AlertProvider>
      <AppContent />
    </AlertProvider>
  </ThemeProvider>
);

export default App;
